import { __decorate } from "tslib";
import { html } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { emit, waitForEvent } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/event.js';
import { getFormDataEntries } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/formdata.js';
import styles from './form.scss.js';
/**
 * @param Base The base class.
 *
 * @returns form component.
 *
 * @slot submit - <cipublic-button>
 * @slot - form elements
 *
 * @csspart form - the form element
 */
export class Form extends CipublicElement {
    constructor() {
        super(...arguments);
        /**
         * The URL that processes the form submission.
         *
         * This value can be overridden by a formaction attribute on a
         * `<button>`, `<input type="submit">`, or `<input type="image">` element.
         */
        this.action = '';
        /**
         * Space-separated character encodings the server accepts.
         *
         * The browser uses them in the order in which they are listed.
         *
         * The default value means the same encoding as the page.
         */
        this.charset = 'UTF-8';
        /**
         * Enable emit of connected-callback event
         */
        this.emitConnectedCallback = false;
        /**
         * If the value of the method attribute is post, enctype is the MIME type of the form submission. Possible values:
         *
         * `application/x-www-form-urlencoded`: The default value.
         *
         * `multipart/form-data`: Use this if the form contains `<input>` elements with type=file.
         *
         * `text/plain`: Introduced by HTML5 for debugging purposes.
         *
         * This value can be overridden by formenctype attributes on `<button>`, `<input type="submit">`,
         * or `<input type="image">` elements.
         *
         * Default value is set to `multipart/form-data` on this component because we are using `formdata` objects which
         * will transmit data as if the form's encoding type were set to `multipart/form-data`.
         */
        this.enctype = 'multipart/form-data';
        /**
         * The HTTP method to submit the form with. Possible (case insensitive) values:
         *
         * `post`: The `POST` method; form data sent as the request body.
         *
         * `get`: The `GET` method; form data appended to the action URL with a ? separator.
         * Use this method when the form has no side-effects.
         *
         * `dialog`: When the form is inside a `<dialog>`, closes the dialog on submission.
         *
         * This value can be overridden by formenctype attributes on `<button>`, `<input type="submit">`,
         * or `<input type="image">` elements.
         */
        this.method = 'POST';
        /**
         * This Boolean attribute indicates that the form shouldn't be validated when submitted.
         *
         * If this attribute is not set (and therefore the form is validated), it can be overridden by a formnovalidate
         * attribute on a `<button>`, `<input type="submit">`, or `<input type="image">` element belonging to the form.
         */
        this.novalidate = false;
        /**
         * Setting as `true` removes the default `submit` event listeners on `form-registration.ts`.
         */
        this.override = false;
        /**
         * Indicates where to display the response after submitting the form.
         *
         * It is a name/keyword for a browsing context (for example, tab, window, or iframe).
         *
         * The following keywords have special meanings:
         *
         * `_self` (default): Load into the same browsing context as the current one.
         *
         * `_blank`: Load into a new unnamed browsing context.
         *
         * `_parent`: Load into the parent browsing context of the current one. If no parent, behaves the same as _self.
         *
         * `_top`: Load into the top-level browsing context (i.e., the browsing context that is an ancestor of
         * the current one and has no parent). If no parent, behaves the same as `_self`.
         *
         * This value can be overridden by formenctype attributes on `<button>`, `<input type="submit">`,
         * or `<input type="image">` elements.
         */
        this.target = '_self';
    }
    static get styles() {
        return [styles];
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.emitConnectedCallback) {
            emit(this, 'connected-callback');
        }
    }
    render() {
        const { action, autocomplete, charset, enctype, method, name, novalidate, override, rel, target } = this;
        return html `<form accept-charset="${ifDefined(charset)}" action="${ifDefined(action)}" autocomplete="${ifDefined(autocomplete)}" enctype="${ifDefined(enctype)}" method="${ifDefined(method)}" name="${ifDefined(name)}" ?novalidate="${novalidate}" ?override="${override}" rel="${ifDefined(rel)}" target="${ifDefined(target)}" @submit="${() => {
            waitForEvent(this, 'formdata-complete', true)
                .then(async (detail) => {
                const data = detail.formDataEvent.formData;
                return getFormDataEntries(data);
            })
                .then(() => {
                if (!this.querySelector('.error')) {
                    emit(document.body, 'analytics-form-submit', {
                        detail: {
                            targetAction: action || '',
                            targetForm: this,
                            targetName: name || '',
                        },
                    });
                }
            });
        }}"><slot></slot></form>`;
    }
}
__decorate([
    property({ reflect: true })
], Form.prototype, "action", void 0);
__decorate([
    property({ reflect: true })
], Form.prototype, "autocomplete", void 0);
__decorate([
    property({ reflect: true, attribute: 'accept-charset' })
], Form.prototype, "charset", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Form.prototype, "emitConnectedCallback", void 0);
__decorate([
    property({ reflect: true })
], Form.prototype, "enctype", void 0);
__decorate([
    query('form')
], Form.prototype, "_form", void 0);
__decorate([
    property({ reflect: true })
], Form.prototype, "method", void 0);
__decorate([
    property({ reflect: true })
], Form.prototype, "name", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Form.prototype, "novalidate", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Form.prototype, "override", void 0);
__decorate([
    property({ reflect: true })
], Form.prototype, "rel", void 0);
__decorate([
    property({ reflect: true })
], Form.prototype, "target", void 0);
//# sourceMappingURL=form.js.map